/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * The US time zones offered in the booking "Pick a time" step. `value` is the IANA id (what
 * `Intl.DateTimeFormat` needs to render slot times); `label` is the friendly US name shown to
 * the patient — never the raw IANA id (US apps say "Eastern Time (New York)", not "America/New_York").
 * All US zones are covered: ET, CT, MT, Arizona (no DST), PT, Alaska, Hawaii.
 */
export type TimeZoneOption = { value: string; label: string }

export const US_TIME_ZONES: TimeZoneOption[] = [
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "America/Phoenix", label: "Arizona (Phoenix, no DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska Time (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (Honolulu)" },
  // Non-US zones are hidden for the US launch — kept here for when we expand:
  // { value: "Europe/London", label: "London (GMT/BST)" },
  // { value: "Europe/Belgrade", label: "Central European Time (CET)" },
]

const DEFAULT_US_ZONE = "America/New_York"

/**
 * Resolve a browser-detected IANA zone to a supported US zone. A US patient's zone is one of
 * the list above; anything else (e.g. a developer abroad, or an uncommon IANA alias) falls back
 * to Eastern so the US-only picker and the rendered slot times always stay consistent.
 */
export const resolveUsTimeZone = (zone: string): string =>
  US_TIME_ZONES.some((option) => option.value === zone) ? zone : DEFAULT_US_ZONE

/**
 * Friendly US label for a zone (e.g. "Eastern Time (New York)") — the exact label the patient picked on
 * the Time step, so selection and confirmation always read the same. Unsupported zones resolve to
 * the default first, so this always returns a real label.
 */
export const usTimeZoneLabel = (zone: string): string => {
  const resolved = resolveUsTimeZone(zone)
  return US_TIME_ZONES.find((option) => option.value === resolved)?.label ?? resolved
}

/**
 * Short form of the friendly label — the tz name without the city qualifier
 * (e.g. "Pacific Time (Los Angeles)" → "Pacific Time"). Used inline next to a time.
 */
export const usTimeZoneShortLabel = (zone: string): string => usTimeZoneLabel(zone).split(" (")[0]
